/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.13.0
 */

import { Fragment, useCallback } from 'react';

import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardExpensesDetail,
} from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import type { IWizardExpensesDetailTranslation } from '@modules/internationalization/types/wizards/expensesDetail';
import Wizard from '@modules/wizard/components/Wizard';
import { EWizardName } from '@modules/wizard/wizardConstants';
import type { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import type { IRootReducer } from '@reducers/rootReducer';

import { wizardExpensesDetailHandleNextAction } from '../wizardExpensesDetailActions';
import { EWizardExpensesDetailSteps } from '../wizardExpensesDetailConstant';
import wizardExpensesDetailReducer, { IWizardExpensesDetailReducer } from '../wizardExpensesDetailReducer';
import { makeSelectWizardExpensesDetailValidation } from '../wizardExpensesDetailSelectors';
import WizardExpensesDetailConfirmation from './WizardExpensesDetailConfirmation';
import WizardExpensesDetailSelectParams from './WizardExpensesDetailSelectParams';

const WizardExpensesDetail = (props: IConnectedProps & IDispatchProps) => {

    const {
        stepName,
        translationExpensesDetail,
        validation,

        handleNext,
    } = props;

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { repCorpExpenses } } = translationCommon

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardExpensesDetailSteps.StepSelectParams:
                return <WizardExpensesDetailSelectParams />;
            case EWizardExpensesDetailSteps.StepConfirmation:
                return <WizardExpensesDetailConfirmation />;
        }
    }, [stepName]);

    return (
        <Fragment>
            <PageLocale lazy
                fileNames={[
                    TranslationFileNamesEnum.wizardExpensesDetail,
                    TranslationFileNamesEnum.wizard,
                    TranslationFileNamesEnum.common,
                ]} />

            <Title pageName={ECommonTranslationPages.wizardExpensesDetail} />

            <Wizard
                basketType={EBasketType.EXPENSES_ACCOUNTS}
                instructionLink={repCorpExpenses}
                isNextDisabled={!validation.isValid}
                name={EWizardName.EXPENSES_DETAIL}
                translationProps={{
                    error: translationExpensesDetail.error,
                    steps: translationExpensesDetail.steps,
                    title: translationExpensesDetail.title,
                }}

                validateError={validation.error}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>

        </Fragment>
    );
};

interface IConnectedProps extends Pick<IWizardState, 'stepName'>,
    Pick<IWizardExpensesDetailReducer, 'validation'> {
    translationExpensesDetail: IWizardExpensesDetailTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    stepName: makeSelectWizardStepName(),
    translationExpensesDetail: makeSelectTranslationWizardExpensesDetail(),
    validation: makeSelectWizardExpensesDetailValidation(),
});

export interface IDispatchProps {
    handleNext: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardExpensesDetailHandleNextAction()),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardExpensesDetail),
    { reducer: { wizardExpensesDetail: wizardExpensesDetailReducer } },
);
